import { createFileRoute, Link } from "@tanstack/react-router";
import { QrCode, ShieldCheck, Timer } from "lucide-react";
import { Button } from "@client/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title:
          "African Karate Federation Referees Committee | In-Hall Exam System",
      },
      {
        name: "description",
        content:
          "Synchronised in-hall karate referee examinations: question bank, exam versions, QR entry, server-controlled timing and automatic grading.",
      },
      {
        property: "og:title",
        content: "African Karate Federation Referees Committee",
      },
      {
        property: "og:description",
        content:
          "Run synchronised in-hall referee examinations with QR entry, server-authoritative timing and automatic grading.",
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white p-2.5 shadow-lg border border-white/30">
              <img
                src="/UFAK_Karate_Africa_Vertical.svg"
                alt="African Karate Federation"
                className="h-full w-full object-contain"
              />
            </div>
            <div>
              <p className="hall-heading text-sm font-semibold text-primary-foreground">
                African Karate Federation
              </p>
              <p className="hall-heading text-xl font-bold">
                Referees Committee
              </p>
            </div>
          </div>
          <Button asChild variant="secondary" size="sm">
            <Link to="/auth">Staff sign in</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-16">
        <p className="hall-heading text-sm font-semibold text-accent">
          Phase 1 — In-hall pilot
        </p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight text-foreground sm:text-5xl">
          A disciplined examination terminal for karate referee certification
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
          Referees enter the hall, scan a QR code and identify themselves. The
          instructor starts the examination once and every candidate advances
          together on a timeline the server controls.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <Link to="/auth">Staff sign in</Link>
          </Button>
        </div>

        <section className="mt-16 grid gap-6 md:grid-cols-3">
          {[
            {
              icon: QrCode,
              title: "QR-based entry",
              body: "No candidate accounts. A secure, non-guessable session token admits referees to the hall session only.",
            },
            {
              icon: Timer,
              title: "Server-authoritative timing",
              body: "Every question uses the same duration and advances automatically from the official start timestamp.",
            },
            {
              icon: ShieldCheck,
              title: "Automatic grading",
              body: "Attempts auto-submit and grade. Results and activity timelines are visible to staff only.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <article key={title} className="panel p-6">
              <Icon className="size-6 text-accent" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                {title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </article>
          ))}
        </section>
      </main>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        <p>
          Referees receive the examination link or QR code from the instructor
          inside the hall.
        </p>
        <p className="mt-2">Powered by Ptahelix Intelligent Solutions</p>
      </footer>
    </div>
  );
}
